// src/data/blitzQuiz.js
// Blitzabfrage bei einem Angriff eines Freundes: 10 Auswahl-Fragen aus dem, was man
// schon kann. Vokabeln ab Stufe 3 (aus allen Wochen) und schon richtig konjugierte
// Verbformen. Falsch beantwortete Vokabeln werden für morgen zur Wiederholung fällig.

import { supabase } from "../supabaseClient";
import { getSessionUser } from "../lib/sessionUser";
import { loadVocabPair } from "./loadVocabPair";
import { loadConjugations } from "./loadConjugations";
import { loadProgress, loadProgressRemote, saveProgressEntries } from "./progressStorage";
import { parseLanguageId, pronounFor, PERSON_SLOTS } from "./languages";

export const BLITZ_QUESTIONS = 10;
export const BLITZ_SECONDS = 15;
export const BLITZ_PASS_RATIO = 0.8;
const VERB_SHARE = 0.3; // bis zu 3 von 10 Fragen sind Verbformen
const MIN_BOX = 3;
const FALLBACK_WORDS = 150; // Anfänger ohne Fortschritt: die häufigsten Wörter
const DAY_MS = 24 * 60 * 60 * 1000;

export function passScore(total) {
  return Math.ceil(total * BLITZ_PASS_RATIO);
}

function shuffle(list, rng) {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickDistractors(answer, candidates, count, rng) {
  const seen = new Set([answer.toLowerCase()]);
  const result = [];
  for (const c of shuffle(candidates, rng)) {
    const key = (c || "").trim().toLowerCase();
    // "-" = Form gibt es nicht (unpersönliche Verben wie llover)
    if (!key || key === "-" || seen.has(key)) continue;
    seen.add(key);
    result.push(c);
    if (result.length === count) break;
  }
  return result;
}

/**
 * Stellt die Fragen zusammen (rein, ohne Laden – testbar).
 *   words       – Vokabel-Paar [{ id, type, source, target, freqRank }]
 *   progress    – Fortschritt (id → { box, ... })
 *   conjugations – Map verbId → { "1s": …, … } der Lernsprache (source)
 *   verbForms   – Map verbId → Set der Personen, die schon einmal richtig waren
 *   sourceLang  – Lernsprache (für die Pronomen)
 * Rückgabe: [{ kind, id, prompt, hint, answer, options }]
 */
export function buildBlitzQuestions({
  words,
  progress,
  conjugations = new Map(),
  verbForms = new Map(),
  sourceLang,
  count = BLITZ_QUESTIONS,
  rng = Math.random,
}) {
  const byId = new Map(words.map((w) => [w.id, w]));

  // Vokabeln: zuerst Stufe 3–5, dann niedrigere Stufen, ohne Fortschritt die häufigsten Wörter
  const known = Object.entries(progress || {})
    .filter(([id, s]) => byId.has(id) && (s?.box ?? 0) >= MIN_BOX)
    .map(([id]) => byId.get(id));
  const started = Object.entries(progress || {})
    .filter(([id, s]) => byId.has(id) && (s?.box ?? 0) > 0 && (s?.box ?? 0) < MIN_BOX)
    .map(([id]) => byId.get(id));
  const fallback = [...words].sort((a, b) => a.freqRank - b.freqRank).slice(0, FALLBACK_WORDS);

  // Verbformen: nur Verben mit Konjugation und mindestens einer schon richtigen Form
  const verbItems = [];
  for (const [verbId, slots] of verbForms) {
    const forms = conjugations.get(verbId);
    const word = byId.get(verbId);
    if (!forms || !word) continue;
    for (const slot of slots) {
      if (forms[slot]) verbItems.push({ verbId, slot, forms, word });
    }
  }

  const verbCount = Math.min(verbItems.length, Math.round(count * VERB_SHARE));
  const chosenVerbs = shuffle(verbItems, rng)
    // höchstens eine Form pro Verb, sonst verraten sich die Antworten gegenseitig
    .filter((item, i, all) => all.findIndex((o) => o.verbId === item.verbId) === i)
    .slice(0, verbCount);

  const vocabNeeded = count - chosenVerbs.length;
  const vocabPool = [];
  const used = new Set();
  for (const group of [shuffle(known, rng), shuffle(started, rng), shuffle(fallback, rng)]) {
    for (const w of group) {
      if (vocabPool.length >= vocabNeeded) break;
      if (used.has(w.id)) continue;
      used.add(w.id);
      vocabPool.push(w);
    }
  }

  const vocabQuestions = vocabPool.map((w, i) => {
    // abwechselnd Lernsprache → Muttersprache und umgekehrt
    const toTarget = i % 2 === 0;
    const sameType = words.filter((o) => o.type === w.type && o.id !== w.id);
    const answer = toTarget ? w.target : w.source;
    const distractors = pickDistractors(
      answer,
      sameType.map((o) => (toTarget ? o.target : o.source)),
      3,
      rng
    );
    return {
      kind: "vocab",
      id: w.id,
      prompt: toTarget ? w.source : w.target,
      hint: toTarget ? "Was heißt das?" : "Wie sagt man das?",
      answer,
      options: shuffle([answer, ...distractors], rng),
    };
  });

  const verbQuestions = chosenVerbs.map(({ verbId, slot, forms, word }) => {
    const answer = forms[slot];
    // Falsche Antworten: andere Personen desselben Verbs, notfalls gleiche Person anderer Verben
    const sameVerb = PERSON_SLOTS.filter((s) => s !== slot).map((s) => forms[s]);
    let distractors = pickDistractors(answer, sameVerb, 3, rng);
    if (distractors.length < 3) {
      const others = [...conjugations.values()].map((f) => f[slot]);
      distractors = [
        ...distractors,
        ...pickDistractors(answer, others.filter((o) => !distractors.includes(o)), 3 - distractors.length, rng),
      ];
    }
    return {
      kind: "verb",
      id: verbId,
      prompt: `${pronounFor(sourceLang, slot)} · ${word.source}`,
      hint: `Konjugiere (${word.target})`,
      answer,
      options: shuffle([answer, ...distractors], rng),
    };
  });

  return shuffle([...vocabQuestions, ...verbQuestions], rng).filter(
    (q) => q.options.length >= 2
  );
}

// Frühere, spanisch benannte Personen-Schlüssel (wie in verbProgressStorage.js)
const LEGACY_FORM_KEYS = { yo: "1s", tu: "2s", el: "3s", nosotros: "1p", vosotros: "2p", ellos: "3p" };

// Verbformen, die schon einmal richtig waren, aus allen Wochen (Supabase, sonst Browser-Speicher).
// Nur Präsens: Aufgaben in anderen Zeitformen ("es-v-0005@preterite", verbTasks.js) zählen nicht.
async function loadKnownVerbForms(languageId, profileId) {
  const result = new Map();
  const add = (verbId, forms) => {
    if (String(verbId).includes("@")) return;
    for (const [key, f] of Object.entries(forms || {})) {
      if (!f?.everCorrect) continue;
      const slot = LEGACY_FORM_KEYS[key] || key;
      if (!result.has(verbId)) result.set(verbId, new Set());
      result.get(verbId).add(slot);
    }
  };

  try {
    const user = (await getSessionUser()).data.user;
    if (user) {
      const { data, error } = await supabase
        .from("verb_progress")
        .select("verb_id, forms")
        .eq("user_id", user.id)
        .eq("profile_id", profileId || "default")
        .eq("language_id", languageId);
      if (!error) {
        for (const row of data || []) add(row.verb_id, row.forms);
        return result;
      }
    }
  } catch (e) {
    console.warn("blitzQuiz: verb_progress failed:", e);
  }

  try {
    const prefix = `fluent_verb_progress_${profileId || "default"}_${languageId}_w`;
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;
      const states = JSON.parse(window.localStorage.getItem(key) || "{}");
      for (const [verbId, state] of Object.entries(states)) add(verbId, state?.forms);
    }
  } catch (e) {
    console.warn("blitzQuiz: local verb progress failed:", e);
  }
  return result;
}

/**
 * Lädt alles für die Blitzabfrage und stellt die Fragen zusammen.
 * Rückgabe: { questions, progress } (progress = Stand, auf dem die Fragen beruhen)
 */
export async function loadBlitzQuiz(languageId, profileId) {
  const { source, target } = parseLanguageId(languageId) || { source: "es", target: "de" };

  const user = (await getSessionUser()).data.user;
  const [words, conjugations, verbForms, remote] = await Promise.all([
    loadVocabPair(source, target),
    loadConjugations(source),
    loadKnownVerbForms(languageId, profileId),
    user ? loadProgressRemote(user.id, languageId, profileId) : Promise.resolve(null),
  ]);
  const progress = remote || loadProgress(languageId, profileId);

  const questions = buildBlitzQuestions({
    words,
    progress,
    conjugations,
    verbForms,
    sourceLang: source,
  });
  return { questions, progress };
}

/**
 * Falsch beantwortete Vokabeln morgen wiederholen: Termin auf morgen, Stufe bleibt.
 * Nur Wörter, die schon im Fortschritt stehen.
 */
export function scheduleWrongWords(languageId, profileId, progress, wrongIds, now = Date.now()) {
  const entries = {};
  for (const id of wrongIds) {
    const state = progress?.[id];
    if (!state) continue;
    entries[id] = { ...state, srStage: 1, srDueAt: now + DAY_MS };
  }
  saveProgressEntries(languageId, profileId, entries);
  return Object.keys(entries);
}
